import React, { useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile.ts";
import {
    Card, CardContent, Typography, Grid, Box,
    LinearProgress, Tabs, Tab, Divider
} from "@mui/material";
import { Chip } from "@mui/material";
import { ImageUploader } from "./ImageUploader.tsx";

// ---------------------------------------------------------
//   TYPES
// ---------------------------------------------------------
interface Player {
    id: number;
    nickname: string;
    shirtNumber: number;
    roles: string[];
}

interface Participant {
    id: number;
    setNumber: number;
    player: Player;
    role: string;
    starter: boolean;
}

interface SetDTO {
    id: number;
    setNumber: number;
    teamScore: number;
    opponentScore: number;
}

interface MatchDTO {
    id: number;
    date: string;
    opponent: string;
    finalResult: string;
    competitionType: string;
    note: string;
    sets: SetDTO[];
    participants: Participant[];
    teamPhoto?: string;
    reportPhoto?: string;
}

interface MatchCardProps {
    match: MatchDTO;
}

// ---------------------------------------------------------
//   TAB PANEL
// ---------------------------------------------------------
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div hidden={value !== index}>
        {value === index && <Box sx={{ mt: 2 }}>{children}</Box>}
    </div>
);

const roleColors: Record<string, string> = {
    BANDA: "#A5D6A7",
    PALLEGGIO: "#90CAF9",
    OPPOSTO: "#FFCC80",
    LIBERO: "#EF9A9A",
    CENTRALE: "#CE93D8",
};

// ---------------------------------------------------------
//   PLAYER CHIP
// ---------------------------------------------------------
const PlayerChip = ({ p }: { p: Participant }) => (
    <Chip
        label={`${p.starter ? "⭐ " : ""}${p.player.shirtNumber} · ${p.player.nickname} (${p.role})`}
        size="small"
        sx={{
            borderRadius: "8px",
            fontSize: "0.75rem",
            padding: "4px 8px",
            backgroundColor: roleColors[p.role] || "#e0e0e0",
            fontWeight: p.starter ? "bold" : "normal",
        }}
    />
);

// ---------------------------------------------------------
//   COMPONENTE PRINCIPALE
// ---------------------------------------------------------
export const MatchCard: React.FC<MatchCardProps> = ({ match }) => {
    const isMobile = useIsMobile();
    const [tabIndex, setTabIndex] = useState(0);

    const teamPhotoUrl = `${process.env.REACT_APP_BASE_URL}matches/${match.id}/images/team_photo.jpg`;
    const reportPhotoUrl = `${process.env.REACT_APP_BASE_URL}matches/${match.id}/images/report_photo.jpg`;

    const [teamPhoto, setTeamPhoto] = useState(teamPhotoUrl);
    const [reportPhoto, setReportPhoto] = useState(reportPhotoUrl);

    const participantsBySet: Record<number, Participant[]> = {};
    match.participants.forEach((p) => {
        if (!participantsBySet[p.setNumber]) participantsBySet[p.setNumber] = [];
        participantsBySet[p.setNumber].push(p);
    });

    const getSetColor = (teamScore: number, opponentScore: number) => {
        if (teamScore > opponentScore) return "success.main";
        if (teamScore < opponentScore) return "error.main";
        return "grey.500";
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ minHeight: { xs: "auto", sm: 470 }, display: "flex", flexDirection: { xs: "column", sm: "row" } }}>

                {/* ------------------  TABS  ------------------ */}
                <Tabs
                    orientation={isMobile ? "horizontal" : "vertical"}
                    variant="scrollable"
                    scrollButtons="auto"
                    value={tabIndex}
                    onChange={(e, v) => setTabIndex(v)}
                    sx={{
                        borderRight: { xs: 0, sm: 1 },
                        borderBottom: { xs: 1, sm: 0 },
                        borderColor: "divider",
                        minWidth: { xs: "unset", sm: 140 },
                        mr: { xs: 0, sm: 2 },
                        mb: { xs: 1, sm: 0 },
                    }}
                >
                    <Tab label="Summary" />
                    {match.sets.map((s) => (
                        <Tab
                            key={s.id}
                            label={`Set ${s.setNumber}`}
                            sx={{ color: getSetColor(s.teamScore, s.opponentScore) }}
                        />
                    ))}
                </Tabs>

                {/* ------------------  CONTENUTO TAB  ------------------ */}
                <Box sx={{ flex: 1 }}>

                    {/* SUMMARY */}
                    <TabPanel value={tabIndex} index={0}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            🏆 vs {match.opponent} – {match.finalResult}
                        </Typography>
                        <Typography variant="body2">📅 {match.date}</Typography>
                        <Typography variant="body2">📘 {match.competitionType}</Typography>

                        <Typography variant="subtitle2">Note: {match.note}</Typography>

                        <Box sx={{ display: "flex", gap: 3, mt: 3, flexWrap: "wrap" }}>
                            <ImageUploader
                                matchId={match.id}
                                photoType="team_photo"
                                imageUrl={teamPhoto}
                                onUpload={() => setTeamPhoto(`${teamPhoto}?t=${Date.now()}`)}
                            />
                            <ImageUploader
                                matchId={match.id}
                                photoType="report_photo"
                                imageUrl={reportPhoto}
                                onUpload={() => setReportPhoto(`${reportPhoto}?t=${Date.now()}`)}
                            />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            {match.sets.map((s) => (
                                <Box key={s.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <Box sx={{ flex: 1, mr: 1 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                (s.teamScore + s.opponentScore) === 0
                                                    ? 0
                                                    : (s.teamScore / (s.teamScore + s.opponentScore)) * 100
                                            }
                                            sx={{
                                                height: 20,
                                                borderRadius: 1,
                                                backgroundColor: "grey.300",
                                                "& .MuiLinearProgress-bar": {
                                                    backgroundColor: getSetColor(s.teamScore, s.opponentScore)
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ minWidth: 40 }}>
                                        {s.teamScore}-{s.opponentScore}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </TabPanel>

                    {/* SET DETAIL */}
                    {match.sets.map((s, idx) => {
                        const players = participantsBySet[s.setNumber] || [];

                        const starters = players.filter(p => p.starter && p.role !== "LIBERO");
                        const liberos = players.filter(p => p.role === "LIBERO");
                        const bench = players.filter(p => !p.starter && p.role !== "LIBERO");

                        return (
                            <TabPanel key={s.id} value={tabIndex} index={idx + 1}>

                                {/* Barra punteggio */}
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <Box sx={{ flex: 1, mr: 1 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                (s.teamScore + s.opponentScore) === 0
                                                    ? 0
                                                    : (s.teamScore / (s.teamScore + s.opponentScore)) * 100
                                            }
                                            sx={{
                                                height: 20,
                                                borderRadius: 1,
                                                backgroundColor: "grey.300",
                                                "& .MuiLinearProgress-bar": {
                                                    backgroundColor: getSetColor(s.teamScore, s.opponentScore)
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ minWidth: 40 }}>
                                        {s.teamScore}-{s.opponentScore}
                                    </Typography>
                                </Box>

                                {/* Titolari */}
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: "bold", opacity: 0.8 }}
                                >
                                    Titolari
                                </Typography>
                                <Grid container spacing={1}>

                                    {starters.map((p) => (
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={p.id}>
                                            <PlayerChip p={p} />
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Divider → Libero */}
                                {liberos.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, fontWeight: "bold", opacity: 0.8 }}
                                        >
                                            Libero
                                        </Typography>
                                    </>
                                )}

                                {/* Libero */}
                                <Grid container spacing={1}>
                                    {liberos.map((p) => (
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={p.id}>
                                            <PlayerChip p={p} />
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Divider → Subentrati */}
                                {bench.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, fontWeight: "bold", opacity: 0.8 }}
                                        >
                                            Sostituzioni
                                        </Typography>
                                    </>
                                )}

                                {/* Subentrati */}
                                <Grid container spacing={1}>
                                    {bench.map((p) => (
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={p.id}>
                                            <PlayerChip p={p} />
                                        </Grid>
                                    ))}
                                </Grid>

                            </TabPanel>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};
